import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { openStream } from "@/api/stream";
import { useEnvStore } from "@/stores/env";
import { PageHeader } from "@/components/page-header";
import { LiveDot } from "@/components/live-dot";
import { StreamList } from "@/components/stream/stream-list";
import { StreamEventDetail } from "@/components/stream/stream-event-detail";
import { EmptyState } from "@/components/empty-state";
import { MousePointerClick } from "lucide-react";
import type { EventSummary, RunSummary } from "@/api/types";

export const Route = createFileRoute("/_authed/stream")({
  component: StreamPage,
});

const MAX_EVENTS = 200;

export function StreamPage() {
  const [events, setEvents] = useState<EventSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string>();
  const [reconnecting, setReconnecting] = useState(false);

  const activeSlug = useEnvStore((s) => s.activeSlug);

  useEffect(() => {
    // Reconnect (and drop the previous env's events) whenever the environment
    // changes so the stream only shows the selected environment.
    setEvents([]);
    setSelectedId(undefined);
    const close = openStream(
      {
        onEvent: (e) => {
          setEvents((prev) => {
            if (prev.some((p) => p.id === e.id)) return prev;
            return [e, ...prev].slice(0, MAX_EVENTS);
          });
          setReconnecting(false);
        },
        onRun: (_r: RunSummary) => {
          // Runs are refetched via the event-scoped query in StreamEventDetail;
          // no local state needed here.
        },
        onOpen: () => setReconnecting(false),
        onError: () => setReconnecting(true),
      },
      activeSlug,
    );
    return close;
  }, [activeSlug]);

  // Default the view to the newest event (top of the list) until the user
  // pins one by clicking; then their choice sticks.
  const effectiveId = selectedId ?? events[0]?.id;
  const selected = events.find((e) => e.id === effectiveId);

  return (
    <div className="flex h-full flex-col space-y-6">
      <PageHeader eyebrow="Monitor" title="Stream">
        <div className="flex items-center gap-1.5">
          <LiveDot color="var(--brand)" live />
          <span className="font-mono text-[11px] text-muted-foreground">
            {reconnecting ? "reconnecting…" : "streaming"}
          </span>
        </div>
      </PageHeader>

      <div className="grid min-h-0 flex-1 grid-cols-[minmax(280px,340px)_1fr] gap-4">
        <div className="overflow-hidden rounded-md border bg-card">
          <StreamList items={events} selectedId={effectiveId} onSelect={setSelectedId} />
        </div>
        <div className="overflow-y-auto rounded-md border bg-card p-4">
          {selected ? (
            <StreamEventDetail event={selected} />
          ) : (
            <EmptyState icon={<MousePointerClick size={28} />} title="Select an event" />
          )}
        </div>
      </div>
    </div>
  );
}
